import { useEffect, useRef, useState, type CompositionEvent } from "react";
import { Input, ScrollView, Text, View, YStack } from "tamagui";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse, AxiosError } from "axios";
import useDebounce from "../../hooks/useDebounce";
import Alert from "@components/Alert";
import AddressSearchResult from "./AddressSearchResult";
import InlineLoading from "@components/loading/InlineLoading";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useAddressStore } from "@utils/store/utils/useAddressStore";
import { getCurrentLocation } from "./func/addressTool";
import { handleAPIGeocodeGetAddress } from "./API/handleAddressAPI";
import { MdMyLocation } from "react-icons/md";
import { isLocalAddressDevBypassEnabled } from "./func/devAddressFallback";
import { colors } from "@styles/colors";

type GpsStatus = "locating" | "success" | "failed";

const AddressAutoComplete = () => {
  const { lineAccessToken } = useLineAuth();
  const { setAutocompleteAPIData, autocompleteAPIData, setAutoCompleteAddress, setAddressViewMode, setMapPickerDisabled } = useAddressStore();
  const isLocalDevBypass = isLocalAddressDevBypassEnabled();
  const [searchAddress, setSearchAddress] = useState<string>("");
  const [searchList, setSearchList] = useState<any[]>([]);
  const [inlineLoading, setInlineLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>("locating");
  const [gpsAddress, setGpsAddress] = useState<Record<string, any> | null>(null);
  const hasMounted = useRef(false);
  /** 注音／中文輸入法是否正在組字中（組字期間不觸發 autocomplete） */
  const isComposingRef = useRef(false);

  const debouncedSearchAddress = useDebounce(searchAddress, 500);

  //-- GPS 自動定位（mount 後背景執行）
  const handleGPSLocate = async () => {
    setGpsStatus("locating");
    const locResult = await getCurrentLocation();
    if (locResult.state !== "success") {
      setGpsStatus("failed");
      return;
    }
    const lat = Number(locResult.lat);
    const lng = Number(locResult.lng);
    const geocodeRes = await handleAPIGeocodeGetAddress({ lat, lng }, lineAccessToken || "");
    if (!geocodeRes || geocodeRes.state !== "success" || !geocodeRes.possibleList?.length) {
      setGpsStatus("failed");
      return;
    }
    setGpsAddress({ ...(geocodeRes.possibleList[0] as Record<string, any>), latitude: lat, longitude: lng });
    setGpsStatus("success");
  };

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    handleGPSLocate();
  }, []);

  //-- 點選 GPS 定位結果 → 帶入表單
  const handleSelectGPSAddress = () => {
    if (!gpsAddress) return;
    setAutoCompleteAddress(gpsAddress);
    setMapPickerDisabled(false);
    setAddressViewMode("addressForm");
  };

  //-- Autocomplete API
  const handleAPIAutoComplete = (address: string, token: string) => {
    setInlineLoading(true);
    setHasError(false);
    let APIUrl = `address-normalization-api/address/autocomplete?address=${address}`;
    if (autocompleteAPIData?.sessionToken) {
      APIUrl += `&sessionToken=${autocompleteAPIData?.sessionToken}`;
    }
    handleRequest({
      asyncRequest: () =>
        api.get(APIUrl, {
          baseURLType: "ADDRESS",
          headers: { Authorization: `Bearer ${token}` },
        }),
      successCallback: (res: AxiosResponse) => {
        const { data } = res.data;
        const apiData = { ...autocompleteAPIData, type: data?.API };
        if (data?.possibleList) setSearchList(data?.possibleList);
        if (data?.sessionToken) apiData.sessionToken = data?.sessionToken;
        setAutocompleteAPIData(apiData);
      },
      errorCallback: (error: any) => {
        if (isLocalDevBypass && error.response?.status === 401) {
          console.warn("[AddressDebug][AddressAutoComplete] bypass autocomplete 401 in local dev");
          setSearchList([]);
          setHasError(false);
          return;
        }
        setHasError(true);
        console.error(error);
      },
      finalCallback: () => setInlineLoading(false),
    });
  };

  useEffect(() => {
    if (!debouncedSearchAddress) {
      setSearchList([]);
    } else {
      handleAPIAutoComplete(debouncedSearchAddress, lineAccessToken || "");
    }
  }, [debouncedSearchAddress]);

  const gpsRoute = gpsAddress?.route || "";
  const gpsStreetNumber = gpsAddress?.street_number || "";
  const gpsCity = gpsAddress?.administrative_area_level_1 || "";
  const gpsArea = gpsAddress?.administrative_area_level_2 || "";

  return (
    <>
      <View padding="$5" paddingTop="$4" background="#fff">
        <YStack width="100%" gap="$2">
          {/* 搜尋框 */}
          <Input
            flex={1}
            placeholder="搜尋地址"
            borderColor={hasError ? "$red10" : undefined}
            onChangeText={(text: string) => {
              //-- 注音組字中不送出：避免「覺ㄌ」這類半形字串打到 autocomplete API
              if (isComposingRef.current) return;
              setSearchAddress(text);
            }}
            // Tamagui Input 型別未宣告 IME composition events，runtime 仍會轉到原生 input
            {...({
              onCompositionStart: () => {
                isComposingRef.current = true;
              },
              onCompositionEnd: (e: CompositionEvent<HTMLInputElement>) => {
                isComposingRef.current = false;
                //-- 組字結束才把最終字串送進 debounce
                setSearchAddress(e.currentTarget.value);
              },
            } as object)}
          />
          {hasError && <Alert text="無法辨識此地址，請輸入完整且正確的資訊" state="error" />}
          {isLocalDevBypass && !lineAccessToken && debouncedSearchAddress ? (
            <Alert text="本地開發模式：已略過地址搜尋授權，可直接使用下方手動搜尋" state="warning" />
          ) : null}
        </YStack>

        {/* GPS 定位 btn */}
        {gpsStatus === "locating" && (
          <div className="flex items-center gap-3 px-3 py-3 mt-3 rounded-xl border border-gray-200 bg-gray-50">
            <MdMyLocation className="text-blue-400 w-5 h-5 flex-shrink-0 animate-pulse" />
            <span className="text-sm text-gray-400">定位中...</span>
          </div>
        )}

        {gpsStatus === "success" && gpsAddress && (
          <button
            type="button"
            onClick={handleSelectGPSAddress}
            className="flex items-center gap-3 px-3 py-3 mt-3 rounded-xl border border-blue-200 bg-blue-50 w-full text-left active:bg-blue-100 transition-colors">
            <MdMyLocation className="text-blue-500 w-5 h-5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-sm text-gray-800 leading-snug">
                {gpsRoute}
                {gpsStreetNumber}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {gpsCity}
                {gpsArea}
              </div>
            </div>
          </button>
        )}
      </View>
      <ScrollView showsVerticalScrollIndicator={false}>
        <YStack background={colors.bg} className="gap-3" padding="$5" height="100%">
          {searchList?.length <= 0 && (
            <YStack alignItems="center">
              <Text fontSize={30}>📍</Text>
              <Text color={colors.gray} fontWeight="bold">
                搜尋或定位配送地址
              </Text>
              <Text fontSize={14} color={colors.gray}>
                選擇後可在地圖上微調並補充門牌號資訊
              </Text>
            </YStack>
          )}
          {/* 搜尋結果 */}
          {inlineLoading ? (
            <div className="mt-5">
              <InlineLoading />
            </div>
          ) : (
            <AddressSearchResult searchList={searchList} searchWord={debouncedSearchAddress} hasError={hasError} />
          )}
        </YStack>
      </ScrollView>
    </>
  );
};

export default AddressAutoComplete;
